import { Fragment, useState, useEffect } from "react"
import Breadcrumb from "../../layout/breadcrumb"
import { Container, Row, Col, Card, CardHeader, CardBody, Button, Table, Spinner } from "reactstrap"
import ApexCharts from "react-apexcharts"
import { Users, Book, UserCheck, FileText, Award, Clock, CheckCircle, Calendar, Filter } from "react-feather"
import authAxios from "../../services/Token"
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format, subDays } from 'date-fns';
import { es } from 'date-fns/locale';
import './Dashboard.css';
import Swal from 'sweetalert2';

const Default = () => {
  // Date range filter states
  const [startDate, setStartDate] = useState(subDays(new Date(), 30));
  const [endDate, setEndDate] = useState(new Date());
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [filters, setFilters] = useState({
    fecha_inicio: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
    fecha_fin: format(new Date(), 'yyyy-MM-dd')
  });

  // Estados para datos educativos
  const [stats, setStats] = useState({
    totalEstudiantes: 0,
    totalDocentes: 0,
    totalCursos: 0,
    totalEvaluaciones: 0,
    cursosActivos: 0,
    estudiantesActivos: 0,
    promedioAprobacion: 0,
    materiasCompletadas: 0
  });

  const [cursosData, setCursosData] = useState([]);
  const [estudiantesData, setEstudiantesData] = useState([]);
  const [progresoCursos, setProgresoCursos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] = useState({
    loading: true,
    options: {
      chart: {
        type: 'bar',
        height: 350,
        toolbar: { 
          show: true,
          tools: {
            download: true,
            selection: true,
            zoom: true,
            zoomin: true,
            zoomout: true,
            pan: true,
            reset: true
          }
        }
      },
      colors: ['#4680ff', '#0e9f6e'],
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '55%',
          borderRadius: 4,
        }
      },
      dataLabels: {
        enabled: false
      },
      stroke: {
        show: true,
        width: 2,
        colors: ['transparent']
      },
      xaxis: {
        categories: [],
      },
      yaxis: {
        title: {
          text: 'Cantidad'
        }
      },
      fill: {
        opacity: 1
      },
      tooltip: {
        y: {
          formatter: function (val) {
            return val
          }
        }
      },
      legend: {
        position: 'top',
        horizontalAlign: 'center'
      }
    },
    series: [
      {
        name: 'Nuevos Estudiantes',
        data: []
      },
      {
        name: 'Nuevos Cursos',
        data: []
      }
    ]
  });

  const [pieChartData, setPieChartData] = useState({
    series: [0, 0, 0],
    options: {
      chart: {
        type: 'pie',
        height: 350
      },
      labels: ['Cursos en Progreso', 'Cursos Completados', 'Cursos Sin Iniciar'],
      colors: ['#4680ff', '#0e9f6e', '#f05252'],
      responsive: [{
        breakpoint: 480,
        options: {
          chart: {
            width: 300
          },
          legend: {
            position: 'bottom'
          }
        }
      }],
      plotOptions: {
        pie: {
          donut: {
            size: '45%'
          }
        }
      }
    }
  });

  // Cargar datos del dashboard educativo
  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Obtener datos del dashboard general
      const dashboardResponse = await authAxios.get("/docente/estudiantes-generales", {
        params: {
          fecha_inicio: filters.fecha_inicio,
          fecha_fin: filters.fecha_fin
        }
      });

      if (dashboardResponse.data.success) {
        const dashboardData = dashboardResponse.data.data;
        
        // Actualizar estados con los datos reales del backend
        setStats({
          totalEstudiantes: dashboardData.estadisticas_generales.total_estudiantes,
          totalDocentes: dashboardData.estadisticas_generales.total_docentes,
          totalCursos: dashboardData.estadisticas_generales.total_cursos,
          totalEvaluaciones: dashboardData.estadisticas_generales.total_evaluaciones,
          cursosActivos: dashboardData.estadisticas_generales.cursos_activos,
          estudiantesActivos: dashboardData.estadisticas_generales.estudiantes_activos,
          promedioAprobacion: dashboardData.estadisticas_generales.promedio_aprobacion,
          materiasCompletadas: dashboardData.estadisticas_generales.materias_completadas
        });

        setCursosData(dashboardData.cursos_populares || []);
        setEstudiantesData(dashboardData.estudiantes_destacados || []);
        setProgresoCursos(dashboardData.progreso_cursos || []);

        // Actualizar gráfico de barras con datos reales
        if (dashboardData.graficos && dashboardData.graficos.estadisticas_mensuales) {
          setChartData(prev => ({
            ...prev,
            loading: false,
            options: {
              ...prev.options,
              xaxis: {
                categories: dashboardData.graficos.estadisticas_mensuales.labels
              }
            },
            series: [
              {
                name: 'Nuevos Estudiantes',
                data: dashboardData.graficos.estadisticas_mensuales.nuevos_estudiantes
              },
              {
                name: 'Nuevos Cursos',
                data: dashboardData.graficos.estadisticas_mensuales.nuevos_cursos
              }
            ]
          }));
        }

        // Actualizar gráfico de pie con datos reales
        if (dashboardData.graficos && dashboardData.graficos.estado_cursos) {
          setPieChartData(prev => ({
            ...prev,
            series: [
              dashboardData.graficos.estado_cursos.en_progreso,
              dashboardData.graficos.estado_cursos.completados,
              dashboardData.graficos.estado_cursos.sin_iniciar
            ]
          }));
        }
      } else {
        throw new Error(dashboardResponse.data.message);
      }

    } catch (error) {
      console.error('Error al cargar datos del dashboard:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudieron cargar los datos del dashboard',
        confirmButtonColor: '#4680ff'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [filters]);

  const applyDateRange = () => {
    if (startDate && endDate) {
      const startAtNoon = new Date(startDate);
      startAtNoon.setHours(12, 0, 0, 0);

      const endAtNoon = new Date(endDate);
      endAtNoon.setHours(12, 0, 0, 0);

      setFilters({
        fecha_inicio: format(startAtNoon, 'yyyy-MM-dd'),
        fecha_fin: format(endAtNoon, 'yyyy-MM-dd')
      });
      setShowDatePicker(false);
    }
  };

  const resetDateRange = () => {
    const newStartDate = subDays(new Date(), 30);
    const newEndDate = new Date();
    
    setStartDate(newStartDate);
    setEndDate(newEndDate);
    setFilters({
      fecha_inicio: format(newStartDate, 'yyyy-MM-dd'),
      fecha_fin: format(newEndDate, 'yyyy-MM-dd')
    });
    setShowDatePicker(false);
  };

  // Cards de estadísticas
  const StatCard = ({ icon: Icon, title, value, color, subtitle }) => (
    <Col xl="3" lg="6" md="6" sm="12" className="mb-4">
      <Card className="card-hover shadow-sm">
        <CardBody>
          <div className="d-flex align-items-center">
            <div className={`avatar avatar-stats p-50 m-0 me-3 bg-light-${color}`}>
              <Icon size={24} className={`text-${color}`} />
            </div>
            <div className="flex-grow-1">
              <h2 className="fw-bolder mb-0">{value}</h2>
              <p className="card-text mb-1">{title}</p>
              {subtitle && <small className="text-muted">{subtitle}</small>}
            </div>
          </div>
        </CardBody>
      </Card>
    </Col>
  );

  // Función para formatear fecha
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return format(new Date(dateString), 'dd/MM/yyyy', { locale: es });
  };

  // Función para determinar el estado del curso
  const getCursoStatus = (fechaFinal) => {
    if (!fechaFinal) return { badge: 'secondary', text: 'No definido' };
    
    const hoy = new Date();
    const fechaFin = new Date(fechaFinal);
    
    if (fechaFin >= hoy) {
      return { badge: 'success', text: 'Activo' };
    } else {
      return { badge: 'secondary', text: 'Finalizado' };
    }
  };

  return (
    <Fragment>
      <Breadcrumb parent="Dashboard" title="Panel Educativo" />
      <Container fluid={true}>
        {/* Date Range Picker */}
        <Row className="mb-3">
          <Col>
            <Card>
              <CardBody className="py-2">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="mb-0">
                      <Filter size={16} className="me-2" />
                      Filtros de Fecha
                    </h6>
                    <small className="text-muted">
                      {format(new Date(filters.fecha_inicio), 'dd/MM/yyyy', { locale: es })} - {format(new Date(filters.fecha_fin), 'dd/MM/yyyy', { locale: es })}
                    </small>
                  </div>
                  <div>
                    <Button 
                      color="primary" 
                      size="sm" 
                      onClick={() => setShowDatePicker(!showDatePicker)}
                      className="me-2"
                    >
                      <Calendar size={14} className="me-1" />
                      Seleccionar Fechas
                    </Button>
                    <Button 
                      color="outline-secondary" 
                      size="sm" 
                      onClick={resetDateRange}
                    >
                      Restablecer
                    </Button>
                  </div>
                </div>

                {showDatePicker && (
                  <div className="mt-3 p-3 border rounded bg-light">
                    <Row>
                      <Col md="5">
                        <label className="form-label">Fecha Inicio</label>
                        <DatePicker
                          selected={startDate}
                          onChange={date => setStartDate(date)}
                          selectsStart
                          startDate={startDate}
                          endDate={endDate}
                          className="form-control"
                          locale={es}
                          dateFormat="dd/MM/yyyy"
                        />
                      </Col>
                      <Col md="5">
                        <label className="form-label">Fecha Fin</label>
                        <DatePicker
                          selected={endDate}
                          onChange={date => setEndDate(date)}
                          selectsEnd
                          startDate={startDate}
                          endDate={endDate}
                          minDate={startDate}
                          className="form-control"
                          locale={es}
                          dateFormat="dd/MM/yyyy"
                        />
                      </Col>
                      <Col md="2" className="d-flex align-items-end">
                        <Button 
                          color="primary" 
                          size="sm" 
                          onClick={applyDateRange}
                          className="w-100"
                        >
                          Aplicar
                        </Button>
                      </Col>
                    </Row>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Cards de Estadísticas */}
        <Row>
          <StatCard
            icon={Users}
            title="Total Estudiantes"
            value={stats.totalEstudiantes}
            color="primary"
            subtitle={`${stats.estudiantesActivos} activos`}
          />
          <StatCard
            icon={UserCheck}
            title="Docentes"
            value={stats.totalDocentes}
            color="success"
            subtitle="En plataforma"
          />
          <StatCard
            icon={Book}
            title="Cursos"
            value={stats.totalCursos}
            color="info"
            subtitle={`${stats.cursosActivos} activos`}
          />
          <StatCard
            icon={FileText}
            title="Evaluaciones"
            value={stats.totalEvaluaciones}
            color="warning"
            subtitle="Total realizadas"
          />
        </Row>

        <Row>
          {/* Gráfico de Barras - Progreso Estudiantil */}
          <Col xl="8" lg="7" md="12" sm="12">
            <Card>
              <CardHeader className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                  <Users size={18} className="me-2" />
                  Actividad Mensual - Último Año
                </h5>
                <Button color="outline-primary" size="sm" onClick={fetchDashboardData}>
                  Actualizar
                </Button>
              </CardHeader>
              <CardBody>
                {loading ? (
                  <div className="d-flex justify-content-center align-items-center" style={{ height: '350px' }}>
                    <Spinner color="primary" />
                    <span className="ms-2">Cargando gráfico...</span>
                  </div>
                ) : (
                  <ApexCharts
                    options={chartData.options}
                    series={chartData.series}
                    type="bar"
                    height={350}
                  />
                )}
              </CardBody>
            </Card>
          </Col>

          {/* Gráfico de Pie - Estado de Cursos */}
          <Col xl="4" lg="5" md="12" sm="12">
            <Card>
              <CardHeader>
                <h5 className="mb-0">
                  <Book size={18} className="me-2" />
                  Estado de Cursos
                </h5>
              </CardHeader>
              <CardBody>
                {loading ? (
                  <div className="d-flex justify-content-center align-items-center" style={{ height: '350px' }}>
                    <Spinner color="primary" />
                    <span className="ms-2">Cargando datos...</span>
                  </div>
                ) : (
                  <div>
                    <ApexCharts
                      options={pieChartData.options}
                      series={pieChartData.series}
                      type="pie"
                      height={350}
                    />
                    <div className="mt-3 text-center">
                      <small className="text-muted">
                        Total: {pieChartData.series.reduce((a, b) => a + b, 0)} cursos
                      </small>
                    </div>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row>
          {/* Tabla de Cursos Populares */}
          <Col xl="6" lg="6" md="12" sm="12">
            <Card>
              <CardHeader className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                  <Book size={18} className="me-2" />
                  Cursos Más Populares
                </h5>
                <Button color="primary" size="sm">
                  Ver Todos
                </Button>
              </CardHeader>
              <CardBody>
                <Table responsive hover>
                  <thead>
                    <tr>
                      <th>Curso</th>
                      <th>Estudiantes</th>
                      <th>Fecha Inicio</th>
                      <th>Fecha Fin</th>
                      <th>Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="5" className="text-center">
                          <Spinner size="sm" color="primary" /> Cargando...
                        </td>
                      </tr>
                    ) : cursosData.length > 0 ? (
                      cursosData.map((curso, index) => {
                        const status = getCursoStatus(curso.fecha_final);
                        return (
                          <tr key={curso.id || index}>
                            <td>
                              <div>
                                <strong>{curso.descripcion}</strong>
                                <br />
                                <small className="text-muted">{curso.codigo}</small>
                              </div>
                            </td>
                            <td>
                              <span className="badge bg-primary rounded-pill">
                                {curso.total_estudiantes || 0}
                              </span>
                            </td>
                            <td>
                              <small>{formatDate(curso.fecha_inicio)}</small>
                            </td>
                            <td>
                              <small>{formatDate(curso.fecha_final)}</small>
                            </td>
                            <td>
                              <span className={`badge bg-${status.badge} rounded-pill`}>
                                {status.text === 'Activo' && <Clock size={12} className="me-1" />}
                                {status.text === 'Finalizado' && <CheckCircle size={12} className="me-1" />}
                                {status.text}
                              </span>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="5" className="text-center text-muted">
                          No hay datos de cursos disponibles
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </CardBody>
            </Card>
          </Col>

          {/* Tabla de Estudiantes Destacados */}
          <Col xl="6" lg="6" md="12" sm="12">
            <Card>
              <CardHeader className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                  <Award size={18} className="me-2" />
                  Estudiantes Destacados
                </h5>
                <Button color="primary" size="sm">
                  Ver Todos
                </Button>
              </CardHeader>
              <CardBody>
                <Table responsive hover>
                  <thead>
                    <tr>
                      <th>Estudiante</th>
                      <th>Cédula</th>
                      <th>Promedio</th>
                      <th>Evaluaciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="4" className="text-center">
                          <Spinner size="sm" color="primary" /> Cargando...
                        </td>
                      </tr>
                    ) : estudiantesData.length > 0 ? (
                      estudiantesData.map((estudiante, index) => (
                        <tr key={estudiante.id || index}>
                          <td>
                            <div>
                              <strong>{estudiante.nombre}</strong>
                              <br />
                              <small className="text-muted">{estudiante.correo}</small>
                            </div>
                          </td>
                          <td>
                            <small>{estudiante.numero_documento || 'N/A'}</small>
                          </td>
                          <td>
                            <span className={`fw-bold ${
                              estudiante.promedio_general >= 80 ? 'text-success' : 
                              estudiante.promedio_general >= 60 ? 'text-warning' : 'text-danger'
                            }`}>
                              {estudiante.promedio_general || 0}%
                            </span>
                          </td>
                          <td>
                            <span className="badge bg-info rounded-pill">
                              {estudiante.total_evaluaciones || 0}
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" className="text-center text-muted">
                          No hay datos de estudiantes disponibles
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row>
          {/* Métricas de Rendimiento */}
          <Col xl="4" lg="4" md="6" sm="12">
            <Card>
              <CardHeader>
                <h5 className="mb-0">Rendimiento General</h5>
              </CardHeader>
              <CardBody>
                <div className="text-center">
                  <div className="mb-4">
                    <h1 className="display-4 text-primary">{stats.promedioAprobacion}%</h1>
                    <p className="text-muted">Promedio de Aprobación</p>
                  </div>
                  <div className="row">
                    <div className="col-6">
                      <div className="border-end">
                        <h4 className="text-success">{stats.materiasCompletadas}</h4>
                        <small className="text-muted">Materias Completadas</small>
                      </div>
                    </div>
                    <div className="col-6">
                      <h4 className="text-info">{stats.cursosActivos}</h4>
                      <small className="text-muted">Cursos en Progreso</small>
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>

          {/* Progreso de Cursos */}
          <Col xl="8" lg="8" md="6" sm="12">
            <Card>
              <CardHeader>
                <h5 className="mb-0">Progreso de Cursos</h5>
              </CardHeader>
              <CardBody>
                {loading ? (
                  <div className="text-center">
                    <Spinner color="primary" />
                    <span className="ms-2">Cargando progreso...</span>
                  </div>
                ) : progresoCursos.length > 0 ? (
                  <div className="activity-timeline">
                    {progresoCursos.slice(0, 4).map((curso, index) => (
                      <div key={curso.id || index} className="activity-item d-flex align-items-start mb-3">
                        <div className="activity-badge bg-primary me-3" style={{ width: '8px', height: '8px', borderRadius: '50%', marginTop: '6px' }}></div>
                        <div className="activity-content flex-grow-1">
                          <div className="d-flex justify-content-between align-items-center">
                            <p className="mb-1 fw-bold">{curso.descripcion}</p>
                            <span className="badge bg-light text-dark">
                              {curso.porcentaje_completado || 0}%
                            </span>
                          </div>
                          <div className="progress mb-1" style={{ height: '6px' }}>
                            <div 
                              className="progress-bar bg-success" 
                              role="progressbar" 
                              style={{ 
                                width: `${curso.porcentaje_completado || 0}%` 
                              }}
                            />
                          </div>
                          <small className="text-muted">
                            {curso.materias_completadas || 0} de {curso.total_materias || 0} materias completadas
                          </small>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center text-muted">
                    No hay datos de progreso disponibles
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Resumen de Filtros Aplicados */}
        <Row>
          <Col>
            <Card>
              <CardBody className="py-2">
                <div className="d-flex justify-content-between align-items-center">
                  <small className="text-muted">
                    <strong>Período aplicado:</strong> {format(new Date(filters.fecha_inicio), 'dd/MM/yyyy', { locale: es })} - {format(new Date(filters.fecha_fin), 'dd/MM/yyyy', { locale: es })}
                  </small>
                  <small className="text-muted">
                    <strong>Última actualización:</strong> {format(new Date(), 'dd/MM/yyyy HH:mm', { locale: es })}
                  </small>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </Fragment>
  )
}

export default Default